import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { updateEditor } from "../../apis/apiList/userApi"; // You'll create this API
import { useMessage } from "../../context/MessageContext";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 900,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
};

const EditEditorModal = ({ open, handleClose, editor, onSubmit }) => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [isUpdatingEditor, setIsUpdatingEditor] = useState(false);
  const { showMessage } = useMessage();

  useEffect(() => {
    setForm({
      firstName: editor?.firstName || "",
      lastName: editor?.lastName || "",
      email: editor?.email || "",
      ratePerProject: editor?.ratePerProject?.toString() || "",
      phone: editor?.phone || "",
      twitter: editor?.twitter || "",
      linkedin: editor?.linkedin || "",
      profile_pic: null,
    });
  }, [editor]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "profile_pic") {
      const file = files?.[0];
      if (file && file.type.startsWith("image/")) {
        setForm((prev) => ({ ...prev, profile_pic: file }));
      }
    } else if (name === "ratePerProject") {
      const numericValue = value.replace(/\D/g, "");
      setForm((prev) => ({ ...prev, [name]: numericValue }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setForm((prev) => ({ ...prev, profile_pic: file }));
    }
  };

  const validate = () => {
    let temp = {};
    temp.firstName = form.firstName ? "" : "First name is required";
    temp.lastName = form.lastName ? "" : "Last name is required";
    temp.email = /^\S+@\S+\.\S+$/.test(form.email)
      ? ""
      : "Valid email is required";
    temp.ratePerProject = form.ratePerProject ? "" : "Rate is required";
    temp.phone =
      /^(\+)?[0-9()\-\s]+$/.test(form.phone) && form.phone.length >= 7
        ? ""
        : "Enter a valid phone";
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    console.log("Form Submitted:", form);
    setIsUpdatingEditor(true);
    try {
      const response = await updateEditor(editor._id, form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      onSubmit(response.data);
      showMessage("Editor updated successfully", "success");
      setIsUpdatingEditor(false);
      handleClose();
    } catch (err) {
      console.error("Error updating editor:", err);
      setIsUpdatingEditor(false);
      showMessage(err?.response?.data?.message || 'Something went wrong', "error");
    }
  };

  const handleModalClose = () =>{
    setErrors({});
    handleClose();
  }

  return (
    <Modal open={open} onClose={handleModalClose}>
      <Box sx={style}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Edit Editor</Typography>
          <IconButton onClick={handleModalClose}>
            <Close />
          </IconButton>
        </Box>

        <Grid container spacing={2} mt={1}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Rate Per Project ($)"
              name="ratePerProject"
              value={form.ratePerProject}
              onChange={handleChange}
              error={!!errors.ratePerProject}
              helperText={errors.ratePerProject}
              inputProps={{ inputMode: "numeric" }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Twitter URL"
              name="twitter"
              value={form.twitter}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="LinkedIn URL"
              name="linkedin"
              value={form.linkedin}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById("editProfilePicInput").click()}
              sx={{
                border: "2px dashed #999",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                backgroundColor: dragOver ? "#f0f0f0" : "#fafafa",
                cursor: "pointer",
              }}
            >
              <Typography>
                {form.profile_pic
                  ? `Selected: ${form.profile_pic.name}`
                  : "Drag & drop new profile picture here, or click to change"}
              </Typography>
              <input
                id="editProfilePicInput"
                type="file"
                accept="image/*"
                name="profile_pic"
                hidden
                onChange={handleChange}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={handleModalClose} variant="outlined">Cancel</Button>
              <Button onClick={handleSubmit} variant="contained" disabled={isUpdatingEditor}>{isUpdatingEditor ? "Updating..." : "Update Editor"}</Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default EditEditorModal;
